import React, { useState } from 'react';
import {motion} from 'framer-motion';
import { Link } from 'react-router-dom';
import './App.css';
import FAQSection from './FAQSection';

// Logo component using SVG with proper line breaks and 15% increased text size
const Logo=()=> (
  <svg width="100%" height="100%" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="100" height="100" rx="20" fill="#0E0E0E"/>
    <text x="50" y="36" fontFamily="Arial,sans-serif" fontSize="28" fontWeight="bold" fill="#FCF171" textAnchor="middle">Life</text>
    <text x="50" y="59" fontFamily="Arial,sans-serif" fontSize="28" fontWeight="bold" fill="#FCF171" textAnchor="middle">coach</text>
    <text x="50" y="83" fontFamily="Arial,sans-serif" fontSize="28" fontWeight="bold" fill="#FCF171" textAnchor="middle">assist</text>
    <text x="103" y="103" fontFamily="Arial,sans-serif" fontSize="12" fontWeight="bold" fill="#0E0E0E" textAnchor="middle">™</text>
  </svg>
);

function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    clientCount: '',
    privacyAgreed: false
  });

  const [isRecommendModalOpen, setIsRecommendModalOpen] = useState(false);
  const [recommendFormData, setRecommendFormData] = useState({
    coachName: '',
    coachEmail: '',
    yourName: '',
    yourEmail: '',
    message: 'Please check out this tool that I think could improve coaching.',
    privacyAgreed: false
  });

  const scrollToSection=(sectionId)=> {
    document.getElementById(sectionId)?.scrollIntoView({behavior: 'smooth'});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const webhookUrl = import.meta.env.VITE_WEBHOOK_URL;
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        alert('Thank you for your application! We will be in touch soon.');
        setIsModalOpen(false);
        setFormData({ name: '', email: '', phone: '', businessName: '', clientCount: '', privacyAgreed: false });
      } else {
        alert('There was an error submitting your application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your application. Please try again.');
    }
  };

  const handleRecommendInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRecommendFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRecommendSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const webhookUrl = import.meta.env.VITE_RECOMMEND_WEBHOOK_URL;
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(recommendFormData),
      });
      
      if (response.ok) {
        alert('Thank you for your recommendation!');
        setIsRecommendModalOpen(false);
        setRecommendFormData({
          coachName: '',
          coachEmail: '',
          yourName: '',
          yourEmail: '',
          message: 'Please check out this tool that I found that could improve our work together.',
          privacyAgreed: false
        });
      } else {
        alert('There was an error sending your recommendation. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error sending your recommendation. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-[#FCF171] selection:text-black">
      {/* Hero Section */}
      <section className="relative bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 text-center">
          <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{duration: 0.6}} >
            <div className="mb-10 flex justify-center">
              <img
                src="/LifeAssist-2.png"
                alt="Life Coach Assist / Life Assist"
                className="max-w-xl w-full"
              />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold tracking-tight text-black mb-4">
              Always-Available<br />Tool for Your Clients
            </h1>
            <h2 className="text-2xl lg:text-4xl font-bold text-gray-800 mb-8">
              Increase your service. Decrease your paperwork.
            </h2>
            <p className="text-lg lg:text-3xl text-gray-600 mb-10 max-w-4xl mx-auto leading-relaxed">
              The ultimate value of <i>Life Coach Assist</i> and <i>Life Assist</i> is to <br />improve the effectiveness of your one-on-one coaching and consulting. <br />Human connection is where significant and lasting change occurs.

            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button onClick={()=> setIsModalOpen(true)} className="w-full sm:w-72 bg-[#FCF171] text-black px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#F5EA4A] transition-all duration-300" >
                Use It
              </button>
              <button onClick={()=> setIsRecommendModalOpen(true)} className="w-full sm:w-72 bg-[#FCF171] text-black border-2 border-[#FCF171] hover:bg-[#F5EA4A] hover:border-[#F5EA4A] px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300" >
                Recommend It
              </button>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 text-gray-800">
              <img src="/apple-icon.png" alt="iPhone" className="w-8 h-8" />
              <img src="/android-icon.png" alt="Android" className="w-8 h-8" />
              <span className="font-bold text-lg">Available for iPhone and Android.</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-10 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="mb-10 rounded-xl overflow-hidden shadow-2xl w-full lg:w-1/2 mx-auto">
              <iframe
                className="w-full aspect-video"
                src="https://www.youtube.com/embed/EQ3teL1XZzQ?rel=0&modestbranding=1"
                title="Life Coach Assist Introduction"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </div>
            <h2 className="text-3xl font-bold text-black mb-6">What is <i>Life Coach Assist / Life Assist</i>?</h2>
            <p className="text-xl pb-[20px] text-gray-700 leading-relaxed">
              This tool is available 24/7 to actively listen to your clients as they talk about their progress. It speaks back with a short recap of each thing they say, and then at the end of the contact, it sends you and them a summary of what they said.
            </p>
            <p className="text-xl pb-[20px] text-gray-700 leading-relaxed">
              You barely need to do paperwork. You don't even need to read or save each contact report. When you're ready to coach, look at your dashboard and you'll see their celebrations, actions, and goals along with other valuable information.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              <i>Life Coach Assist / Life Assist</i> is a way for clients to vocalize their thoughts, which helps them get out of their busy minds and moves them to clarity. Speaking is also valuable when they are upset or confused. Thoughts swirl at over 2000 words a minute, but when speaking, our words are less than 200 words a minute and in a more organized fashion.
            </p>
          </div>
          <div className="bg-gray-200 p-8 rounded-2xl border border-gray-300 shadow-xl">
            <h3 className="text-xl font-bold mb-6 text-black">Key Features</h3>
            <ul className="grid md:grid-cols-2 gap-y-4 gap-x-8 list-disc list-inside text-lg text-gray-700">
              <li>Regular English conversations</li>
              <li>Organizes speaking and insights</li>
              <li>Provides data for next session</li>
              <li>Supports all styles of coaching / consulting</li>
              <li>Always available for your clients</li>
              <li>Delivers to you and your client summaries</li>
              <li>Updates a dashboard for your overview</li>
              <li>Tracks goals and commitments</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="pt-10 pb-20 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-black mb-10">Ways to use it</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <ul className="space-y-4 list-disc list-outside ml-5 text-lg text-gray-700">
              <li className="pl-2"><span className="font-semibold text-black">Preparing for sessions</span> – Come prepared with insights and questions.</li>
              <li className="pl-2"><span className="font-semibold text-black">Reviewing previous sessions</span> – Revisit past conversations and insights.</li>
            </ul>
            <ul className="space-y-4 list-disc list-outside ml-5 text-lg text-gray-700">
              <li className="pl-2"><span className="font-semibold text-black">Commitment tracking</span> – Stay in touch with your clients actions and goals.</li>
              <li className="pl-2"><span className="font-semibold text-black">Searching clients shared data</span> – Find relevant information from past conversations.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Benefits by Role Section */}
      <section className="py-20 bg-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <div className="border-b-4 border-[#FCF171] pb-4 mb-6">
                <h3 className="text-2xl font-bold text-black">Benefits for Coaches / Consultants</h3>
              </div>
              <ul className="space-y-4 list-disc list-outside ml-5 text-lg text-gray-700">
                <li className="pl-2">Spend more time in conversations, less time note-taking</li>
                <li className="pl-2">Minimise check-in time and agenda setting in your sessions</li>
                <li className="pl-2">Client updates between sessions</li>
                <li className="pl-2">Dashboard with client's speaking organized by categories and dates</li>
                <li className="pl-2">Custom AI Voices</li>
                <li className="pl-2">Coaching / consulting prompt customization</li>
                <li className="pl-2">Private notes</li>
                <li className="pl-2">Voice-driven queries and notes - coming soon</li>
              </ul>
            </div>

            <div>
              <div className="border-b-4 border-[#FCF171] pb-4 mb-6">
                <h3 className="text-2xl font-bold text-black">Benefits for your Clients</h3>
              </div>
              <ul className="space-y-4 list-disc list-outside ml-5 text-lg text-gray-700">
                <li className="pl-2">Voice-driven conversations</li>
                <li className="pl-2">During conversations clients view it in text message format</li>
                <li className="pl-2">Automatic organization of what client speaks</li>
                <li className="pl-2">Private dashboard for client</li>
                <li className="pl-2">End-of-contact summaries for client and coach</li>
                <li className="pl-2">Recall of the stored information</li>
                <li className="pl-2">Reminders and scheduling - coming soon</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Directions Section */}
      <section className="pt-20 pb-10 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Coach Directions */}
            <div>
              <div className="border-b-4 border-[#FCF171] pb-4 mb-8">
                <h3 className="text-2xl font-bold text-black">Suggestions for you</h3>
              </div>
              <div className="space-y-8">
                {[
                  {
                    title: "Offer it at no cost to all of your clients.",
                    desc: "The more clients use it, the less it costs per client. And, they will likely want more one-to-one paid time with you."
                  },
                  {
                    title: "Contact reports can be reviewed to stay informed.",
                    desc: "They can keep you up to date about client progress. You can also just wait to review the dashboard."
                  },
                  {
                    title: "No need to respond until your next call.",
                    desc: "If the client needs your quick response, have them email or call you."
                  },
                  {
                    title: "Use your dashboard before and during the call.",
                    desc: ""
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-black font-bold text-sm border border-gray-200">
                      {index + 1}
                    </span>
                    <div>
                      <h4 className="text-lg font-bold text-black mb-1">{item.title}</h4>
                      <p className="text-gray-700 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Client Directions */}
            <div>
              <div className="border-b-4 border-[#FCF171] pb-4 mb-8">
                <h3 className="text-2xl font-bold text-black mb-2">Suggestions for your clients</h3>
                <p className="text-lg text-gray-600">(You could send them this or an edited version.)</p>
              </div>
              <div className="space-y-8">
                {[
                  {
                    title: "Contact it often.",
                    desc: "Use it frequently to sort out thoughts."
                  },
                  {
                    title: "Speak in short paragraphs.",
                    desc: "Talk for a few or several minutes."
                  },
                  {
                    title: "Interrupt or mute it.",
                    desc: "It summarizes what was just said but stops when it hears speaking."
                  },
                  {
                    title: "Focus mainly on celebrations and actions.",
                    desc: "It also captures goals, problems, and other information."
                  },
                  {
                    title: "Look at what was spoken and heard.",
                    desc: "The text-like messages show the conversation."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-black font-bold text-sm border border-gray-200">
                      {index + 1}
                    </span>
                    <div>
                      <h4 className="text-lg font-bold text-black mb-1">{item.title}</h4>
                      <p className="text-gray-700 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Memory System Section */}
      <section className="pt-10 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-black mb-6">
              Clients speaking is organized<br />by categories
            </h2>
            <p className="text-xl text-gray-600">
              <i>Life Coach Assist / Life Assist</i> organizes everything it hears into meaningful categories for the client and the coach / consultant. This makes it easy to reflect, retrieve, and review progress over time.
            </p>
          </div>

          <div className="space-y-8">
            {[
              {title: "Celebrations",desc: "Discoveries, accomplishments, gratitudes, milestones, happy times, and whatever else is working well."},
              {title: "Actions",desc: "Past and planned activities, intentions, strategies, and approaches."},
              {title: "Goals",desc: "Desires, results, promises, commitments, and dreams."},
              {title: "Problems",desc: "Breakdowns, challenges, regrets, difficult emotions, life struggles, sad times, and whatever else isn’t working."},
              {title: "Other information",desc: "Important people, dates, thoughts, feelings, and whatever else would be useful to say."}
            ].map((item,index)=> (
              <div key={index} className="flex flex-col md:flex-row md:items-baseline border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                <h3 className="text-xl font-bold text-black w-full md:w-1/3 mb-2 md:mb-0">{item.title}</h3>
                <p className="text-lg text-gray-700 w-full md:w-2/3">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-black mb-12 text-center">How it works</h2>
          <div className="max-w-4xl mx-auto">
            <ol className="relative border-l border-gray-300 ml-4 md:ml-0">
              {[
                {title: "Client talks to the AI assistant",desc: "Engages in natural conversation by speaking."},
                {title: "It captures key insights",desc: "The system listens for important details and context."},
                {title: "Insights are organized",desc: "Information is sorted and stored into the relevant categories."},
                {title: "A summary is generated",desc: "Coach and client get a clear recap after each contact."},
                {title: "Clients personal history grows",desc: "The database becomes a searchable resource for future growth."}
              ].map((step,index)=> (
                <li key={index} className="mb-10 ml-10">
                  <span className="absolute flex items-center justify-center w-8 h-8 bg-[#FCF171] rounded-full -left-4 ring-4 ring-white text-black font-bold text-sm">
                    {index + 1}
                  </span>
                  <h3 className="flex items-center mb-1 text-xl font-bold text-black">
                    {step.title}
                  </h3>
                  <p className="mb-4 text-lg text-gray-600">
                    {step.desc}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pt-20 pb-10 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-10">Pricing</h2>
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#FCF171] bg-opacity-20 p-8 rounded-2xl border border-yellow-200 mb-8 shadow-xl">
              <h3 className="text-2xl font-bold text-black mb-4">Free for the first month</h3>
              <p className="text-xl text-gray-800 leading-relaxed">
                Tiered subscription pricing for individual coaches will be available soon. Enterprise pricing for customization is available upon request.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Section */}
      <section className="pt-10 pb-10 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-black mb-6">Customization</h2>
          <div className="space-y-6 text-xl text-gray-700 leading-relaxed">
            <p>
              Standard greetings and even the style of interactions can be customized. <button onClick={() => setIsModalOpen(true)} className="text-blue-600 hover:underline bg-transparent border-none p-0 cursor-pointer inline">Contact us for details</button>.
            </p>
            <p>
              We can add a knowledge base so that the assistant has access to suggestions you want for your client. This can include books, essays, podcasts, or webinars that you have created. The assistant can go beyond listening to answering questions the way you might answer them. We have an example that uses teachings from one of our co-creators.
            </p>
            <p>
              This assistant can also be customized for other professionals including computer consultants, financial planners, social workers, counselors, recovery sponsors, physical trainers, managers, leaders, and more.
            </p>
          </div>
        </div>
      </section>

      {/* Values-Based Coaching Section */}
      <section className="pt-10 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-8">
            Assists All Coaching / Consulting Styles
          </h2>
          <div className="bg-[#FCF171] bg-opacity-30 p-10 rounded-xl border border-yellow-400 shadow-xl">
            <p className="text-xl font-medium text-gray-800 leading-relaxed">
              <i>Life Coach Assist / Life Assist</i> supports any style of coaching. The assistant supports deeper reflection, greater clarity, and permanent change. Critical and creative thinking is the purpose of this always available AI assistant that facilitates communication from the client to the coach.
            </p>
          </div>
          <p className="text-2xl lg:text-3xl font-semibold text-gray-800 leading-relaxed mt-10 max-w-4xl mx-auto">
            The ultimate purpose of <i>Life Coach Assist / Life Assist</i> is to increase the effectiveness and the amount of time that the client and coach spend speaking together. That human connection is where real change occurs.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white text-center">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-5xl font-bold mb-10">
            Explore <i>Life Coach Assist / Life Assist</i>
          </h2>
          <button onClick={()=> setIsModalOpen(true)} className="bg-[#FCF171] text-black px-12 py-5 rounded-lg font-bold text-xl hover:bg-white transition-colors duration-300">
            Contact Us
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 text-gray-600">
            <Link to="/privacy-policy" className="hover:text-black transition-colors">Privacy Policy</Link>
            <span className="hidden md:inline text-gray-300">|</span>
            <Link to="/terms-of-service" className="hover:text-black transition-colors">Terms of Service</Link>
            <span className="hidden md:inline text-gray-300">|</span>
            <Link to="/support" className="hover:text-black transition-colors">Support</Link>
            <span className="hidden md:inline text-gray-300">|</span>
            <span>© {new Date().getFullYear()} New Breakthroughs, LLC and Footprints Design, LLC</span>
          </div>
          <div className="text-center mt-4 text-gray-500 text-sm">
            Trademarked and Patent Pending
          </div>
        </div>
      </footer>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setIsModalOpen(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-black">Use It</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Fill out the form below to get started.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="businessName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Coaching Business Name *
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    required
                    value={formData.businessName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="Your coaching business name"
                  />
                </div>

                <div>
                  <label htmlFor="clientCount" className="block text-sm font-semibold text-gray-700 mb-2">
                    Approximate number of clients
                  </label>
                  <input
                    type="text"
                    id="clientCount"
                    name="clientCount"
                    value={formData.clientCount}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="e.g. 5-10"
                  />
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="privacyAgreed"
                    name="privacyAgreed"
                    required
                    checked={formData.privacyAgreed}
                    onChange={handleInputChange}
                    className="mt-1 w-4 h-4 text-[#FCF171] border-gray-300 rounded focus:ring-[#FCF171] focus:ring-2 cursor-pointer"
                  />
                  <label htmlFor="privacyAgreed" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    I acknowledge that I have read and agree to the{' '}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      Privacy Policy
                    </Link>
                    {' '}and{' '}
                    <Link
                      to="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      Terms of Service
                    </Link>
                    {' '}*
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#FCF171] text-black px-6 py-4 rounded-lg font-bold text-lg hover:bg-[#F5EA4A] transition-all duration-300 mt-6"
                >
                  Submit Request
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Recommend Modal */}
      {isRecommendModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setIsRecommendModalOpen(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-black">Recommend It</h2>
                <button
                  onClick={() => setIsRecommendModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleRecommendSubmit} className="space-y-5">
                <div>
                  <label htmlFor="coachName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Coach's Name *
                  </label>
                  <input
                    type="text"
                    id="coachName"
                    name="coachName"
                    required
                    value={recommendFormData.coachName}
                    onChange={handleRecommendInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="Coach's name"
                  />
                </div>

                <div>
                  <label htmlFor="coachEmail" className="block text-sm font-semibold text-gray-700 mb-2">
                    Coach's Email *
                  </label>
                  <input
                    type="email"
                    id="coachEmail"
                    name="coachEmail"
                    required
                    value={recommendFormData.coachEmail}
                    onChange={handleRecommendInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="coach@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="yourName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="yourName"
                    name="yourName"
                    required
                    value={recommendFormData.yourName}
                    onChange={handleRecommendInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="yourEmail" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Email *
                  </label>
                  <input
                    type="email"
                    id="yourEmail"
                    name="yourEmail"
                    required
                    value={recommendFormData.yourEmail}
                    onChange={handleRecommendInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message to the coach (add or modify)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={recommendFormData.message}
                    onChange={handleRecommendInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FCF171] focus:border-transparent outline-none transition-all resize-none"
                  ></textarea>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="recommendPrivacyAgreed"
                    name="privacyAgreed"
                    required
                    checked={recommendFormData.privacyAgreed}
                    onChange={handleRecommendInputChange}
                    className="mt-1 w-4 h-4 text-[#FCF171] border-gray-300 rounded focus:ring-[#FCF171] focus:ring-2 cursor-pointer"
                  />
                  <label htmlFor="recommendPrivacyAgreed" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    I acknowledge that I have read and agree to the{' '}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      Privacy Policy
                    </Link>
                    {' '}and{' '}
                    <Link
                      to="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      Terms of Service
                    </Link>
                    {' '}*
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#FCF171] text-black px-6 py-4 rounded-lg font-bold text-lg hover:bg-[#F5EA4A] transition-all duration-300 mt-6"
                >
                  Send Recommendation
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default Home;
